import { GenericForm } from '@neo/blocks/forms/generic-form'
import { cn } from '@neo/lib/utils'
import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { useReactFlow } from '@xyflow/react'
import { useRef } from 'react'
import { v4 } from 'uuid'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { logger } from 'v2/utils/logger'
import { z } from 'zod'
import { intentSpeechSchema } from '../../config/validation.config'
import { useGenerateIntentNameMutation } from '../../queries'
import { useFlowStore } from '../../store'
import { FlowNodeTypes, type GenericNode, type NodeDataBase } from '../../types'
import { isAiAgentV2Type } from '../../utils/ai-agent-v29'

interface EdgeFormProps {
  id: string
  sourceNode?: GenericNode
  closePopover: () => void
}

// Type for node configs that have intents (ai_agent_v2, ai_action, stream, agent_preset_conversation)
type NodeConfigWithIntents = {
  channel?: string
  intents?: Array<{
    id: string
    intent_name?: string
    intent_instructions?: string
    intent_speech?: {
      speech_enabled: boolean
      intent_speech_text?: string
    }
  }>
}

export const EdgeForm = ({ id, sourceNode, closePopover }: EdgeFormProps) => {
  const { getEdge, getNode } = useReactFlow()
  const edge = getEdge(id)
  // Getting the current source node data config as sourceNode is not updated because it is memoized
  const currentSourceNode = getNode(sourceNode?.id as string)
  const data = currentSourceNode?.data.config as NodeConfigWithIntents
  const intent = data.intents?.find((i) => i.id === edge?.sourceHandle)
  const isEditing = useFlowStore((state) => state.isEditing)
  const isStreamNode = currentSourceNode?.type === FlowNodeTypes.stream
  const { isLivekitEnabled } = useLivekitEnabled()

  const defaultValues = useRef({
    name: intent?.intent_name || '',
    description: intent?.intent_instructions || '',
    intent_speech: {
      speech_enabled: intent?.intent_speech?.speech_enabled || false,
      intent_speech_text: intent?.intent_speech?.intent_speech_text || '',
    },
  })

  const generateIntentNameMutation = useGenerateIntentNameMutation()

  const handleSubmit = async (values: {
    name: string
    description: string
    intent_speech: z.infer<typeof intentSpeechSchema>['intent_speech']
  }) => {
    const { flowInstance } = useFlowStore.getState()
    if (!sourceNode?.id || !flowInstance) return

    const getDefaultFallbackName = () => {
      if (isStreamNode) return 'Stream complete'
      if (currentSourceNode?.type === FlowNodeTypes.ai_action) return 'Action executed'
      return 'User responded'
    }
    const defaultFallbackName = getDefaultFallbackName()

    let intentName = values.name.trim() || defaultFallbackName

    if (!isStreamNode) {
      try {
        const existingIntents =
          data.intents
            ?.filter((i) => i.id !== edge?.sourceHandle && i.intent_name && i.intent_instructions)
            ?.map((i) => ({
              intent_name: i.intent_name || '',
              intent_instructions: i.intent_instructions || '',
            })) || []

        const generatedName = await generateIntentNameMutation.mutateAsync({
          instruction: values.description.trim(),
          fallbackName: defaultFallbackName,
          existingIntents,
        })
        intentName = generatedName
      } catch (error) {
        logger.error('Failed to generate intent name, using fallback:', error)
      }
    }

    flowInstance.updateNodeData(sourceNode?.id, {
      config: {
        ...data,
        intents: data.intents?.map((i) =>
          i.id === edge?.sourceHandle
            ? {
                ...i,
                intent_name: intentName,
                intent_instructions: values.description.trim(),
                intent_speech: {
                  speech_enabled: values.intent_speech.speech_enabled,
                  intent_speech_text: values.intent_speech.speech_enabled
                    ? values.intent_speech.intent_speech_text?.trim()
                    : '',
                },
              }
            : i,
        ),
      },
    } as NodeDataBase)

    flowInstance.updateEdge(id, {
      id: v4(),
      source: sourceNode?.id,
      sourceHandle: edge?.sourceHandle,
    })

    closePopover()
  }

  const isCallChannel = data.channel === 'call'
  const showTransitionSpeech =
    isAiAgentV2Type(currentSourceNode?.type) &&
    !isStreamNode &&
    !(isLivekitEnabled && isCallChannel)

  return (
    <div className="p-4 w-[380px]">
      <div className="text-p-600 mb-4">Edit Path</div>
      <GenericForm
        id={`edge-${id}-form`}
        className={cn('gap-4', !isEditing && 'editing-sidebar')}
        fields={[
          {
            name: 'name',
            label: 'Intent Name',
            componentType: 'input',
            zodValidation: z
              .string()
              .min(1, 'Intent name is required')
              .regex(
                /^[a-zA-Z0-9\s]+$/,
                'Intent name must contain only letters, numbers, and spaces',
              ),
            disabled: () => !isEditing,
            inputProps: {
              placeholder: isStreamNode ? 'Stream complete' : 'User responded',
            },
            eventDataExtractor: (e) => e.target?.value.replace(/[^a-zA-Z0-9\s]/g, ''),
          },
          {
            name: 'description',
            label: isStreamNode ? 'Description' : 'Instruction',
            componentType: 'textarea',
            zodValidation: z
              .string()
              .min(1, isStreamNode ? 'Description is required' : 'Instruction is required'),
            disabled: () => !isEditing,
            inputProps: isStreamNode
              ? {
                  placeholder: 'Proceeds when the stream completes successfully',
                }
              : undefined,
          },
          {
            name: 'intent_speech.speech_enabled',
            label: 'Enable transitional response',
            componentType: 'checkbox',
            visible: showTransitionSpeech,
            zodValidation:
              intentSpeechSchema.shape.intent_speech.removeDefault().shape.speech_enabled,
            disabled: () => !isEditing,
            inputProps: {
              helperText: 'The response sent before moving to the next node',
              labelClassName: 'text-p-500 text-primary',
              helperTextClassName: 'text-xs-400 text-secondary',
            },
            afterOnChange: (values, _, form) => {
              if (values.intent_speech?.speech_enabled) {
                form?.setFieldValue('intent_speech.intent_speech_text', 'Let me check that for you')
                form?.validateField('intent_speech.intent_speech_text', 'change')
              }
            },
          },
          {
            name: 'intent_speech.intent_speech_text',
            componentType: 'input',
            inputProps: {
              placeholder: 'Enter transitional response',
            },
            onChangeListenTo: ['intent_speech.speech_enabled'],
            visible: (state) => showTransitionSpeech && state.intent_speech?.speech_enabled,
            disabled: () => !isEditing,
            getDynamicProps: (values) => {
              const isSpeechEnabled = values.intent_speech?.speech_enabled
              if (isSpeechEnabled) {
                return {
                  zodVal: z
                    .string()
                    .refine((val) => val.trim() !== '', {
                      message: 'Transitional response is required',
                    })
                    .refine((val) => val.length <= 500, {
                      message: 'Speech text must be less than 500 characters',
                    }),
                }
              }
              return {
                zodVal:
                  intentSpeechSchema.shape.intent_speech.removeDefault().shape.intent_speech_text,
              }
            },
          },
        ]}
        defaultValues={defaultValues.current}
        onSubmit={handleSubmit}
        includeSubmitButton={false}
      />
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={closePopover}>
          Cancel
        </Button>
        <ButtonWithLoader
          type="submit"
          form={`edge-${id}-form`}
          disabled={!isEditing || generateIntentNameMutation.isPending}
          loading={generateIntentNameMutation.isPending}
        >
          Save
        </ButtonWithLoader>
      </div>
    </div>
  )
}
